import axios from 'axios';

import type { ChargeCheckoutCreditCard } from './charge-checkout-api';
import { env } from './runtime-env';

// Client SEM auth — troca de plano self-service (identidade via handoff_token).
const publicClient = axios.create({
  baseURL: env.apiUrl,
  withCredentials: false,
  timeout: 30_000,
});

export interface EligibleUpgrade {
  plan_id: string;
  name: string;
  monthly_price: string;
  /** `upgrade` cobra a diferença agora; `downgrade` vale no fim do ciclo. */
  direction: 'upgrade' | 'downgrade';
  proration_amount: string;
  days_remaining: number;
  /** Só downgrade (YYYY-MM-DD). */
  scheduled_for: string | null;
  /** Só downgrade. */
  monthly_savings: string | null;
  /** Só no modo renewal: mensalidade cheia (o que a fatura nova cobra). */
  renewal_price?: string;
  /** Só no modo renewal: mensalidade com o desconto vigente aplicado. */
  effective_renewal_price?: string;
}

export interface PlanChangePreview {
  current_plan: { id: string; name: string; monthly_price: string };
  eligible_upgrades: EligibleUpgrade[];
  pending_downgrade: { plan_id: string; scheduled_for: string } | null;
  /** `renewal` = fatura de renovação aberta: trocar substitui a fatura. */
  mode: 'mid_cycle' | 'renewal';
  /** Só renewal: assinatura suspensa — pagar reativa. */
  suspended?: boolean;
  /** Só renewal: 1ª fatura nunca paga — pagar ativa. */
  first_purchase?: boolean;
  /** Só renewal: link pra pagar a renovação do plano ATUAL. */
  current_plan_payment_url?: string | null;
  /** URL do app do SaaS — "voltar ao sistema" quando o SaaS não manda `redirect`. */
  saas_url?: string | null;
  /**
   * Ciclo da assinatura. Opcional: API antiga não manda — ausente = mensal.
   * Em anual, o preço atual, o da fatura substituta e a economia são do ANO.
   */
  billing_cycle?: string;
}

/** Sufixo do preço no ciclo da assinatura ("/ano" só no anual; o resto segue "/mês"). */
export function cycleSuffix(preview: Pick<PlanChangePreview, 'billing_cycle'> | null | undefined): string {
  return preview?.billing_cycle === 'anual' ? '/ano' : '/mês';
}

export interface PlanChangeStartResult {
  applied: boolean;
  /** Só downgrade: data em que o plano menor começa a valer. */
  scheduled_for?: string;
  charge_id: string | null;
  payment_method: 'pix' | 'boleto' | 'credit_card' | null;
  payment_url: string | null;
  proration_amount: string;
  mode?: 'mid_cycle' | 'renewal';
  /** Só renewal: valor da fatura nova (mensalidade com desconto vigente). */
  renewal_amount?: string;
}

export async function fetchPlanChangePreview(token: string): Promise<PlanChangePreview> {
  const { data } = await publicClient.post('/api/public/checkout/plan-change/preview', {
    handoff_token: token,
  });
  return data.data as PlanChangePreview;
}

/**
 * "Manter assinatura": desfaz o cancelamento agendado. Depois disso a prévia
 * volta a funcionar normal (27/10 — antes o agendado caía na compra e virava
 * uma 2ª assinatura cobrada).
 */
export async function keepPlanChangeSubscription(token: string): Promise<{ kept: boolean }> {
  const { data } = await publicClient.post('/api/public/checkout/plan-change/keep', {
    handoff_token: token,
  });
  return data.data as { kept: boolean };
}

export async function startPlanChange(input: {
  token: string;
  to_plan_id: string;
  // Sem boleto: o servidor recusa desde 29/10 (boleto nunca em assinatura).
  payment_method: 'pix' | 'credit_card';
  /** Obrigatório no cartão: o backend cobra na hora, sem sair do nosso checkout. */
  credit_card?: ChargeCheckoutCreditCard;
}): Promise<PlanChangeStartResult> {
  const { data } = await publicClient.post('/api/public/checkout/plan-change', {
    handoff_token: input.token,
    to_plan_id: input.to_plan_id,
    payment_method: input.payment_method,
    ...(input.credit_card ? { credit_card: input.credit_card } : {}),
  });
  return data.data as PlanChangeStartResult;
}
